import { create } from 'zustand'
import { Client, StompSubscription } from '@stomp/stompjs'
import { WS_ENDPOINTS } from '../config/api'

interface WebSocketStore {
  client: Client | null
  isConnected: boolean
  subscriptions: Map<string, StompSubscription>
  connect: (restaurantId: string) => void
  disconnect: () => void
  subscribe: (topic: string, callback: (message: any) => void) => () => void
  isSubscribed: (topic: string) => boolean
}

export const useWebSocketStore = create<WebSocketStore>((set, get) => ({
  client: null,
  isConnected: false,
  subscriptions: new Map(),

  connect: (restaurantId: string) => {
    const currentClient = get().client

    // Don't create a new connection if already connected
    if (currentClient && currentClient.connected) {
      console.log('WebSocket already connected')
      return
    }

    // Disconnect existing client if not connected
    if (currentClient) {
      currentClient.deactivate()
    }

    const client = new Client({
      brokerURL: WS_ENDPOINTS.base,
      connectHeaders: {},
      debug: (str) => {
        console.log('STOMP:', str)
      },
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      onConnect: () => {
        console.log('✅ WebSocket connected globally')
        set({ isConnected: true })
      },
      onDisconnect: () => {
        console.log('❌ WebSocket disconnected')
        set({ isConnected: false })
      },
      onStompError: (frame) => {
        console.error('STOMP error:', frame)
      },
    })

    client.activate()
    set({ client })
  },

  disconnect: () => {
    const { client, subscriptions } = get()

    // Unsubscribe from all topics
    subscriptions.forEach((sub) => sub.unsubscribe())
    set({ subscriptions: new Map() })

    // Disconnect client
    if (client) {
      client.deactivate()
      set({ client: null, isConnected: false })
    }
  },

  subscribe: (topic: string, callback: (message: any) => void) => {
    const { client, subscriptions } = get()

    if (!client || !client.connected) {
      console.warn('Cannot subscribe: WebSocket not connected')
      return () => {}
    }

    // Check if already subscribed
    if (subscriptions.has(topic)) {
      console.log(`Already subscribed to ${topic}`)
      return () => {}
    }

    console.log(`📡 Subscribing to ${topic}`)

    const subscription = client.subscribe(topic, (message) => {
      try {
        const data = JSON.parse(message.body)
        callback(data)
      } catch (error) {
        console.error('Error parsing WebSocket message:', error)
      }
    })

    // Store subscription
    const newSubscriptions = new Map(subscriptions)
    newSubscriptions.set(topic, subscription)
    set({ subscriptions: newSubscriptions })

    // Return unsubscribe function
    return () => {
      console.log(`🔌 Unsubscribing from ${topic}`)
      subscription.unsubscribe()
      const currentSubscriptions = get().subscriptions
      currentSubscriptions.delete(topic)
      set({ subscriptions: new Map(currentSubscriptions) })
    }
  },

  isSubscribed: (topic: string) => {
    return get().subscriptions.has(topic)
  },
}))
